/**
 * I segni delle aziende sulla mappa: quanto sono grandi, come si raggruppano quando si toccano, dove va il nome.
 * Pura, in pixel dello schermo (si ricalcola a ogni ingrandimento).
 *
 * Il segno ha due strati: l'ALONE, di area proporzionale agli account, e il NUCLEO, di area proporzionale alle persone
 * attive. Un'azienda con molti account e poca gente al lavoro ha un alone largo e un nucleo piccolo: si vede senza
 * leggere i numeri.
 */
import type { ContiPerLivello, LivelloMappa } from '@shared/admin/mappaAziende';

const ORDINE_LIVELLI: readonly LivelloMappa[] = ['free', 'base', 'pro', 'business'];

/** Il raggio dell'alone: cresce con la radice degli account (l'area è proporzionale), fra 9 e 30 pixel. */
export function raggioAlone(account: number): number {
  if (account <= 0) return 7;
  return Math.min(30, 8 + 3.6 * Math.sqrt(account));
}

/** Il raggio del nucleo: la sua area sta a quella dell'alone come le persone attive stanno agli account. */
export function raggioNucleo(alone: number, account: number, attive: number): number {
  if (attive <= 0 || account <= 0) return 0;
  return Math.max(3, alone * 0.84 * Math.sqrt(Math.min(1, attive / account)));
}

export interface Arco {
  livello: LivelloMappa;
  /** Da 0 a 1, dalle ore dodici in senso orario. */
  inizio: number;
  fine: number;
}

/** L'anello dell'alone diviso per livello degli account (un'azienda con 4 Business e 1 Pro: 80% e 20%). */
export function archiDeiLivelli(account: ContiPerLivello, ripiego: LivelloMappa): Arco[] {
  const totale = ORDINE_LIVELLI.reduce((s, l) => s + (account[l] ?? 0), 0);
  if (totale <= 0) return [{ livello: ripiego, inizio: 0, fine: 1 }];
  const archi: Arco[] = [];
  let corrente = 0;
  for (const livello of [...ORDINE_LIVELLI].reverse()) {
    const n = account[livello] ?? 0;
    if (n <= 0) continue;
    archi.push({ livello, inizio: corrente, fine: corrente + n / totale });
    corrente += n / totale;
  }
  return archi;
}

/** La somma degli account per livello di più aziende (per il segno di un gruppo). */
export function sommaConti(conti: readonly ContiPerLivello[]): ContiPerLivello {
  const somma: ContiPerLivello = {};
  for (const c of conti) for (const l of ORDINE_LIVELLI) if (c[l]) somma[l] = (somma[l] ?? 0) + (c[l] ?? 0);
  return somma;
}

// ───────────────────────────────────────────────────────────── gruppi

export interface SegnoSulloSchermo {
  chiave: string;
  x: number;
  y: number;
  /** Il peso decide il centro del gruppo e chi resta sopra: gli account. */
  peso: number;
  /** Il raggio disegnato, se non è quello dell'alone (un segno col logo è più grande, una sede secondaria più piccola). */
  raggio?: number;
}

export interface Gruppo {
  chiavi: string[];
  x: number;
  y: number;
  peso: number;
  raggio: number;
}

/**
 * Raggruppa i segni che sullo schermo si toccherebbero: due aziende a 10 km (Cagliari e Sestu) a scala nazionale sono
 * lo stesso punto. Si fondono finché nessuna coppia si sovrappone; il centro è la media pesata.
 */
export function raggruppa(segni: readonly SegnoSulloSchermo[], spazio = 3): Gruppo[] {
  let gruppi: Gruppo[] = segni.map((s) => ({ chiavi: [s.chiave], x: s.x, y: s.y, peso: s.peso, raggio: s.raggio ?? raggioAlone(s.peso) }));
  let fuso = true;
  while (fuso) {
    fuso = false;
    gruppi.sort((a, b) => b.peso - a.peso || a.chiavi[0].localeCompare(b.chiavi[0]));
    esterno: for (let i = 0; i < gruppi.length; i++) {
      for (let j = i + 1; j < gruppi.length; j++) {
        const a = gruppi[i];
        const b = gruppi[j];
        if (Math.hypot(a.x - b.x, a.y - b.y) >= a.raggio + b.raggio + spazio) continue;
        const pa = a.peso + 1;
        const pb = b.peso + 1;
        const peso = a.peso + b.peso;
        const unito: Gruppo = {
          chiavi: [...a.chiavi, ...b.chiavi],
          x: (a.x * pa + b.x * pb) / (pa + pb),
          y: (a.y * pa + b.y * pb) / (pa + pb),
          peso,
          raggio: Math.max(raggioAlone(peso), a.raggio, b.raggio),
        };
        gruppi = [...gruppi.slice(0, i), unito, ...gruppi.slice(i + 1, j), ...gruppi.slice(j + 1)];
        fuso = true;
        break esterno;
      }
    }
  }
  return gruppi;
}

// ───────────────────────────────────────────────────────────── nomi

export interface CandidatoEtichetta {
  chiave: string;
  x: number;
  y: number;
  raggio: number;
  larghezza: number;
  priorita: number;
}

export interface Etichetta {
  chiave: string;
  x: number;
  y: number;
  ancora: 'start' | 'end';
}

interface Scatola {
  x: number;
  y: number;
  l: number;
  a: number;
}

const siToccano = (p: Scatola, q: Scatola) => p.x < q.x + q.l && q.x < p.x + p.l && p.y < q.y + q.a && q.y < p.y + p.a;

function toccaUnCerchio(s: Scatola, c: { x: number; y: number; raggio: number }): boolean {
  const vx = Math.max(s.x, Math.min(c.x, s.x + s.l));
  const vy = Math.max(s.y, Math.min(c.y, s.y + s.a));
  return Math.hypot(c.x - vx, c.y - vy) < c.raggio;
}

/**
 * Dove scrivere i nomi: a destra del segno, se no a sinistra, se no niente (meglio un nome in meno che due nomi
 * sovrapposti). Prima i segni più importanti. Il nome non va sopra un altro segno né fuori dallo schermo.
 */
export function posaEtichette(
  candidati: readonly CandidatoEtichetta[],
  /** Tutti i segni disegnati (anche i gruppi, che non hanno nome): un nome non ci va sopra. */
  ostacoli: ReadonlyArray<{ chiave: string; x: number; y: number; raggio: number }>,
  area: { larghezza: number; altezza: number; margine?: number },
  altezza = 16,
  distacco = 6,
): Etichetta[] {
  const margine = area.margine ?? 4;
  const posate: Scatola[] = [];
  const risultato: Etichetta[] = [];
  const ordinati = [...candidati].sort((a, b) => b.priorita - a.priorita || a.chiave.localeCompare(b.chiave));
  for (const c of ordinati) {
    const prove: Array<{ scatola: Scatola; etichetta: Etichetta }> = [
      {
        scatola: { x: c.x + c.raggio + distacco, y: c.y - altezza / 2, l: c.larghezza, a: altezza },
        etichetta: { chiave: c.chiave, x: c.x + c.raggio + distacco, y: c.y, ancora: 'start' },
      },
      {
        scatola: { x: c.x - c.raggio - distacco - c.larghezza, y: c.y - altezza / 2, l: c.larghezza, a: altezza },
        etichetta: { chiave: c.chiave, x: c.x - c.raggio - distacco, y: c.y, ancora: 'end' },
      },
    ];
    for (const { scatola, etichetta } of prove) {
      const dentro = scatola.x >= margine && scatola.y >= margine
        && scatola.x + scatola.l <= area.larghezza - margine && scatola.y + scatola.a <= area.altezza - margine;
      if (!dentro) continue;
      if (posate.some((p) => siToccano(p, scatola))) continue;
      if (ostacoli.some((o) => o.chiave !== c.chiave && toccaUnCerchio(scatola, o))) continue;
      posate.push(scatola);
      risultato.push(etichetta);
      break;
    }
  }
  return risultato;
}

/**
 * Il nome da scrivere sulla mappa, senza la forma societaria («Fortezza Porte e Finestre S.r.l.» → «Fortezza Porte e
 * Finestre»): sulla carta conta riconoscere l'azienda, e ogni carattere in meno lascia posto a un altro nome. Nel
 * pannello il nome resta intero.
 */
export function nomeBreve(nome: string): string {
  const breve = nome.replace(/[,\s]+(s\.?\s?r\.?\s?l\.?\s?s?\.?|s\.?\s?p\.?\s?a\.?|s\.?\s?n\.?\s?c\.?|s\.?\s?a\.?\s?s\.?|soc(\.|ietà)?\s+coop(\.|erativa)?.*)$/i, '').trim();
  return breve || nome;
}

/** La chiave stabile di un gruppo (o dell'azienda, se è sola): non cambia con l'ordine in cui le aziende si fondono. */
export function chiaveDelGruppo(chiavi: readonly string[]): string {
  return chiavi.length === 1 ? chiavi[0] : `gruppo:${[...chiavi].sort().join(',')}`;
}

/**
 * I comuni di un gruppo, senza ripetizioni, dal più pesante (più account) al più leggero: accanto al numero del gruppo
 * si scrive dove sta («Cagliari e Sestu»), così a scala nazionale si capisce senza avvicinarsi.
 */
export function comuniDelGruppo(membri: ReadonlyArray<{ citta: string | null; peso: number }>): string[] {
  const pesi = new Map<string, number>();
  for (const m of membri) {
    const c = m.citta?.trim();
    if (c) pesi.set(c, (pesi.get(c) ?? 0) + m.peso);
  }
  return [...pesi.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([c]) => c);
}

/**
 * L'arco fra un fornitore e un'azienda che rifornisce: una curva (quadratica) che si alza sempre dallo stesso lato
 * rispetto al verso fornitore → azienda. Le sedi della stessa azienda si uniscono con linee dritte: la curva dice
 * subito «sono due aziende diverse, legate».
 */
export function arcoFra(da: { x: number; y: number }, a: { x: number; y: number }, curvatura = 0.22): string {
  const dx = a.x - da.x;
  const dy = a.y - da.y;
  const lunghezza = Math.hypot(dx, dy);
  if (lunghezza < 1) return `M${da.x} ${da.y}L${a.x} ${a.y}`;
  // La normale a sinistra del verso, lunga una frazione del tratto.
  const cx = (da.x + a.x) / 2 - (dy / lunghezza) * lunghezza * curvatura;
  const cy = (da.y + a.y) / 2 + (dx / lunghezza) * lunghezza * curvatura;
  const r = (v: number) => Math.round(v * 10) / 10;
  return `M${r(da.x)} ${r(da.y)}Q${r(cx)} ${r(cy)} ${r(a.x)} ${r(a.y)}`;
}

/**
 * Il «ventaglio» (come lo «spiderfy» di Leaflet e di Google Maps): segni nello STESSO punto (tre aziende a 200 metri,
 * nella stessa zona industriale) non si staccano mai avvicinandosi, quindi si dispongono in cerchio attorno al punto
 * vero, ognuno col suo filo verso di esso. Mai spostare le coordinate a caso: la mappa direbbe una posizione falsa, e
 * ogni volta diversa.
 *
 * Il cerchio è largo quanto basta perché i segni non si tocchino; si parte dall'alto e si gira in senso orario, in
 * ordine di peso (il più pesante in cima).
 */
export function aVentaglio(centro: { x: number; y: number }, raggi: readonly number[], spazio = 8): Array<{ x: number; y: number }> {
  const n = raggi.length;
  if (n === 0) return [];
  if (n === 1) return [{ ...centro }];
  const maggiore = Math.max(...raggi);
  const perimetro = raggi.reduce((s, r) => s + 2 * r + spazio, 0);
  const raggio = Math.max(maggiore * 1.6 + spazio, perimetro / (2 * Math.PI));
  return raggi.map((_, i) => {
    const angolo = -Math.PI / 2 + (2 * Math.PI * i) / n;
    return { x: centro.x + raggio * Math.cos(angolo), y: centro.y + raggio * Math.sin(angolo) };
  });
}
